<template>
  <div class="login-page">
    <div class="login-box">
      <div class="logo text-center">
        <a href="javascript:void(0);"
          ><img src="@/assets/GhIPSS-logo.png" style="width: 200px"
        /></a>
        <h2 class="col-ghipss">TransView</h2>
      </div>
      <div class="card">
        <div class="body">
          <template v-if="otpSession">
            <OtpVerification
              :key="otpSession.sendCount"
              :email="otpSession.email"
              :otp-prefix="otpSession.otpPrefix"
              :request-id="otpSession.requestId"
              @verified="completeLogin"
              @resend="resendOtp"
              @cancel="cancelOtp"
            />
            <img src="@/assets/background-img.jpg" class="login-illus" alt="" />
          </template>
          <template v-else-if="lockedAccount">
            <AccountLocked
              :email="lockedAccount.email"
              @back="lockedAccount = null"
            />
            <img src="@/assets/background-img.jpg" class="login-illus" alt="" />
          </template>
          <form v-else @submit.prevent="performLogin">
            <div class="msg">Sign in to start your session</div>
            <div class="input-group">
              <span class="input-group-addon">
                <i class="material-icons">email</i>
              </span>
              <div class="form-line">
                <input
                  type="text"
                  class="form-control"
                  v-model="username"
                  placeholder="Email address"
                  autofocus
                  :class="{
                    'is-invalid': v$.username.$error,
                    'is-valid': !v$.username.$invalid,
                  }"
                />
              </div>
              <span v-if="v$.username.$error" class="text-danger">
                {{ v$.username.$errors[0].$message }}
              </span>
            </div>

            <div class="input-group">
              <span class="input-group-addon">
                <i class="material-icons">lock</i>
              </span>
              <div class="form-line">
                <input
                  :type="passwordVisibility.passwordFieldType"
                  v-model="password"
                  class="form-control"
                  placeholder="Password"
                  :class="{
                    'is-invalid': v$.password.$error,
                    'is-valid': !v$.password.$invalid,
                  }"
                />
              </div>
              <span
                v-if="v$.password.$error"
                class="text-danger"
                style="width: 100%"
              >
                {{ v$.password.$errors[0].$message }}
              </span>
              <span class="input-group-addon" @click="switchVisibility">
                <i :class="passwordVisibility.passwordFieldIcon"></i>
              </span>
            </div>

            <div class="row">
              <div class="col-xs-6">
                <button class="btn btn-block btn-ghipss waves-effect">
                  SIGN IN
                </button>
              </div>
              <!-- </div>
                <div class="row m-t-15 m-b--20"> -->
              <div class="col-xs-6 pull-right">
                <router-link to="/forgot_password"
                  ><span class="col-ghipss pull-right"
                    >Forgot Password?</span
                  ></router-link
                >
              </div>
            </div>
            <img src="@/assets/background-img.jpg" class="login-illus" alt="" />
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-page {
  background: none;
}
</style>

<script>
import axios from "@/axios.js";
import useVuelidate from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import OtpVerification from "@/components/OtpVerification.vue";
import AccountLocked from "@/components/AccountLocked.vue";
import { startSession, takeSessionExpiredFlag } from "@/auth.js";

const UNEXPECTED_ERROR = "An unexpected error occurred...Try again!";

// The backend's error message, wherever the response puts it
const errorMessageOf = (data = {}) =>
  data.errorMessage || (data.generalResponse || {}).errorMessage || data.message || "";

// Locked after too many failed attempts (an admin has to reset the password).
// The backend says so in the error message, e.g. "Account is locked. ...",
// whatever the HTTP status; an isLocked flag is also accepted.
const isLocked = (data = {}) =>
  [data.isLocked, data.islocked].some(
    (v) => v === true || String(v).toLowerCase() === "true",
  ) || /\blocked\b/i.test(errorMessageOf(data));
const LOGIN_URL = "user/authenticate_user";
// Request: { requestId }. Response: same shape as LOGIN_URL's.
const RESEND_OTP_URL = "user/resend_otp";

export default {
  setup() {
    return { v$: useVuelidate() };
  },
  name: "Login",
  components: { OtpVerification, AccountLocked },
  data() {
    return {
      username: "",
      password: "",
      // { requestId, email, otpPrefix, sendCount } while on the OTP step
      otpSession: null,
      // { email, message } when the account is locked
      lockedAccount: null,
      passwordVisibility: {
        passwordFieldType: "password",
        passwordFieldIcon: "fa fa-eye",
      },
    };
  },
  validations() {
    return {
      username: {
        required,
        email,
      },
      password: {
        required,
      },
    };
  },
  methods: {
    switchVisibility() {
      this.passwordVisibility.passwordFieldType =
        this.passwordVisibility.passwordFieldType === "password"
          ? "text"
          : "password";
      this.passwordVisibility.passwordFieldIcon =
        this.passwordVisibility.passwordFieldType === "password"
          ? "fa fa-eye"
          : "fa fa-eye-slash";
    },

    // Calls login or resend. On success the backend emails an OTP and returns
    // { requestId, email, otpPrefix }, which starts (or restarts) the OTP step.
    async requestOtp(url, payload) {
      $.LoadingOverlay("show");
      try {
        const response = await axios.post(url, payload);
        const result = response.data || {};
        const general = result.generalResponse || {};
        // Locked accounts stop here, before any OTP step
        if (isLocked(result)) {
          this.showLocked(result, payload);
          return false;
        }
        if (response.status == 200 && general.errorCode == "0") {
          const previous = this.otpSession || {};
          const session = {
            requestId: result.requestId || previous.requestId,
            email: result.email || previous.email || payload.username,
            otpPrefix: result.otpPrefix || previous.otpPrefix,
            // Bumped on every send so the OTP screen resets (boxes, cooldown)
            sendCount: (previous.sendCount || 0) + 1,
          };
          // Verify can't work without a requestId, so stop here instead
          if (!session.requestId) {
            console.error("OTP response is missing requestId:", result);
            this.$swal("Failed", UNEXPECTED_ERROR, "error");
            return false;
          }
          // The prefix is optional: the OTP screen just hides it when missing
          if (!session.otpPrefix) {
            console.warn("OTP response has no otpPrefix:", result);
            session.otpPrefix = "";
          }
          this.otpSession = session;
          return true;
        }
        this.$swal("Failed", general.errorMessage || UNEXPECTED_ERROR, "error");
      } catch (err) {
        console.log(err);
        // Rejections (e.g. 401) may still carry the backend's reason
        const data = (err.response && err.response.data) || {};
        if (isLocked(data)) {
          this.showLocked(data, payload);
          return false;
        }
        this.$swal("Failed", errorMessageOf(data) || UNEXPECTED_ERROR, "error");
      } finally {
        $.LoadingOverlay("hide");
      }
      return false;
    },

    showLocked(result, payload) {
      this.otpSession = null;
      this.lockedAccount = { email: result.email || payload.username || "" };
    },

    async performLogin() {
      this.v$.$validate();
      if (this.v$.$error) return;

      await this.requestOtp(LOGIN_URL, {
        username: this.username,
        password: this.password,
      });
      this.username = "";
      this.password = "";
      this.v$.$reset();
    },

    async resendOtp() {
      const { requestId } = this.otpSession;
      if (await this.requestOtp(RESEND_OTP_URL, { requestId })) {
        this.$swal("Sent", "A new code has been sent to your email.", "success");
      }
    },

    cancelOtp() {
      this.otpSession = null;
    },

    // Runs with the verify-OTP response: check the account, save the session
    // and route by bank/role.
    completeLogin(result) {
      const user = result.userDTO;
      this.cancelOtp();

      if (!user) {
        console.error("Verify-OTP response is missing userDTO:", result);
        this.$swal("Failed", UNEXPECTED_ERROR, "error");
        return;
      }
      //check if user is active
      if (!user.active) {
        this.$swal("Failed", "User account is inactive!", "error");
        return;
      }
      //check if user's bank is active
      if (!user.bankStatus) {
        this.$swal(
          "Failed",
          "Assigned institution is inactive. Please contact the administrator!",
          "error",
        );
        return;
      }

      // UAT sends a token; production sets a cookie instead (no token here)
      startSession(result.token);

      //check if status is activated (after password change)
      if (user.status == "1") {
        localStorage.setItem("userid", user.id);
        localStorage.setItem("email", user.emailAddress);
        localStorage.setItem("bankcode", user.bank);
        localStorage.setItem("bankname", user.bankName);
        localStorage.setItem("fullname", user.fullName);
        localStorage.setItem("rolecode", user.role);
        localStorage.setItem("rolename", user.roleName);
        localStorage.setItem("status", user.status);
        localStorage.setItem("active", user.active);

        if (user.bank == "000001") {
          //ghipss
          this.$router.push("/super_live_outward");
        } else if (user.role == "200") {
          //gifmis
          this.$router.push("/gifmis_transactions");
        } else if (user.role == "201") {
          //admin gifmis
          this.$router.push("/super_gifmis_transactions");
        } else {
          //banks
          if (user.role == "4") {
            this.$router.push("/users");
          } else {
            this.$router.push("/live_outward");
          }
        }
      } else if(user.status == '3'){
        localStorage.setItem("email", user.emailAddress);
        localStorage.setItem("userid", user.id);
        localStorage.setItem("fullname", user.fullName);
        this.$router.push("/password_change");
      }
    },
  },
  mounted() {
    localStorage.clear();
    if (takeSessionExpiredFlag()) {
      this.$swal(
        "Session expired",
        "Your session has ended. Please sign in again.",
        "info",
      );
    }
  },
};
</script>
